import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useReactToPrint } from "react-to-print";
import { Search, Check, X, Eye, Trash2, Printer, Download, MessageCircle, StickyNote, RotateCcw, Pencil, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadPdf, downloadCotizacionPdf } from "@/lib/pdf";
import { openWhatsApp } from "@/lib/whatsapp";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { money, buildIdentificador, fechaMX } from "@/lib/format";
import { PrintHeader } from "@/components/PrintHeader";
import ReporteModulo from "@/components/ReporteModulo";
import DocumentoVenta from "@/components/DocumentoVenta";
import FiltroSelect, { labelDe } from "@/components/FiltroSelect";
import { useVendedoresActivos } from "@/hooks/useVendedoresActivos";

const COT_EST = ["Pendiente de autorización", "Autorizada por el cliente", "Rechazada"];
const cotVend = (c) => c.profiles?.full_name;
const badgeFor = (estatus) => {
  if (estatus === "Autorizada por el cliente") return "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent";
  if (estatus === "Rechazada") return "bg-red-100 text-red-700 hover:bg-red-100 border-transparent";
  if (estatus === "Pendiente de autorización") return "bg-neutral-100 text-neutral-900 hover:bg-neutral-100 border-transparent";
  return "bg-amber-100 text-amber-700 hover:bg-amber-100 border-transparent";
};

export default function Cotizaciones() {
  const { isAdmin } = useAuth();
  const [cots, setCots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [vendSel, setVendSel] = useState("todos");
  const [estSel, setEstSel] = useState("todos");
  const [detailBase, setDetailBase] = useState(null);
  const [verIdx, setVerIdx] = useState("actual");
  const navigate = useNavigate();
  const setDetail = (c) => { setDetailBase(c); setVerIdx("actual"); };
  const versionSel = detailBase && verIdx !== "actual" ? (detailBase.versiones || [])[Number(verIdx)] : null;
  const detail = detailBase ? (versionSel ? { ...detailBase, ...versionSel } : detailBase) : null;
  const [cli, setCli] = useState(null);
  const [listas, setListas] = useState([]);
  const printRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const [notaCot, setNotaCot] = useState(null); // { ...cot, rechazar }
  const [notaTexto, setNotaTexto] = useState("");
  const [notaSaving, setNotaSaving] = useState(false);

  const abrirNota = (c, rechazar = false) => { setNotaCot({ ...c, rechazar }); setNotaTexto(c.nota || ""); };
  const cerrarNota = () => { setNotaCot(null); setNotaTexto(""); };
  const guardarNota = async () => {
    if (!notaCot) return;
    if (notaCot.rechazar && !notaTexto.trim()) return toast.error("Escribe el motivo del rechazo");
    setNotaSaving(true);
    try {
      if (notaCot.rechazar) {
        await api.patch(`/cotizaciones/${notaCot.id}/estatus`, { estatus: "Rechazada", nota: notaTexto });
        toast.success("Cotización rechazada");
      } else {
        await api.patch(`/cotizaciones/${notaCot.id}/nota`, { nota: notaTexto });
        toast.success("Nota guardada");
      }
      cerrarNota(); fetchCots();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setNotaSaving(false); }
  };

  useEffect(() => {
    if (!detailBase?.cliente_id) { setCli(null); return; }
    api.get(`/clientes/${detailBase.cliente_id}`).then(({ data }) => setCli(data)).catch(() => setCli(null));
  }, [detailBase]);

  const fetchCots = async () => {
    try { setLoading(true); const { data } = await api.get("/cotizaciones"); setCots(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchCots(); api.get("/price-lists").then(({ data }) => setListas(data || [])).catch(() => {}); }, []);

  const cambiarEstatus = async (id, estatus) => {
    try {
      const { data } = await api.patch(`/cotizaciones/${id}/estatus`, { estatus });
      toast.success(estatus === "Autorizada por el cliente" ? `Autorizada. Pedido generado: ${data.pedido_generado || "—"}` : `Cotización ${estatus.toLowerCase()}`);
      fetchCots();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    try { await api.delete(`/cotizaciones/${id}`); setCots((p) => p.filter((c) => c.id !== id)); toast.success("Cotización eliminada"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const revertir = async (c) => {
    if (!window.confirm(`¿Revertir la cotización ${c.cot_no} a "Pendiente de autorización"?\n\nSe ELIMINARÁ el pedido, la remisión y la comisión generados por esta autorización. Esta acción no se puede deshacer.`)) return;
    try {
      const { data } = await api.post(`/cotizaciones/${c.id}/revertir`);
      toast.success(`Revertida. Se eliminó: ${(data.pedidos_eliminados || []).join(", ") || "—"}`);
      fetchCots();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  const docName = detail ? `${detail.cot_no}${detail.revision > 0 ? ` Rev ${detail.revision}` : ""}${versionSel ? " (version anterior)" : ""}` : "Cotizacion";
  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: docName });

  const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const grupoFecha = (c) => c.fecha || (c.created_at ? String(c.created_at).slice(0, 10) : null);

  const vendedores = useVendedoresActivos(isAdmin, cots, cotVend);
  const visibles = useMemo(() => cots.filter((c) => {
    if (isAdmin && vendSel !== "todos" && (cotVend(c) || "") !== vendSel) return false;
    if (estSel !== "todos" && c.estatus !== estSel) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.cot_no || "").toLowerCase().includes(s) || (c.cliente || "").toLowerCase().includes(s) || (c.estatus || "").toLowerCase().includes(s);
  }), [cots, vendSel, estSel, search, isAdmin]);
  const grupos = useMemo(() => {
    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth();
    const filtrados = visibles;
    const sortDesc = (a, b) => (grupoFecha(b) || "").localeCompare(grupoFecha(a) || "");
    const mesEnCurso = [], mesesAnio = {}, porAnio = {}, sinFecha = [];
    filtrados.forEach((c) => {
      const f = grupoFecha(c);
      const d = f ? new Date(f + "T00:00:00") : null;
      if (!d || isNaN(d)) { sinFecha.push(c); return; }
      const y = d.getFullYear(), m = d.getMonth();
      if (y === curY && m === curM) mesEnCurso.push(c);
      else if (y === curY) (mesesAnio[m] = mesesAnio[m] || []).push(c);
      else if (y < curY) { (porAnio[y] = porAnio[y] || {}); (porAnio[y][m] = porAnio[y][m] || []).push(c); }
      else (mesesAnio[m] = mesesAnio[m] || []).push(c);
    });
    mesEnCurso.sort(sortDesc); sinFecha.sort(sortDesc);
    Object.values(mesesAnio).forEach((a) => a.sort(sortDesc));
    Object.values(porAnio).forEach((mm) => Object.values(mm).forEach((a) => a.sort(sortDesc)));
    return { mesEnCurso, mesesAnio, porAnio, sinFecha, curY };
  }, [visibles]);
  const mesesAnioKeys = Object.keys(grupos.mesesAnio).map(Number).sort((a, b) => a - b);
  const countMesesAnio = mesesAnioKeys.reduce((s, m) => s + grupos.mesesAnio[m].length, 0);
  const aniosKeys = Object.keys(grupos.porAnio).sort((a, b) => String(b).localeCompare(String(a)));
  const countAnios = aniosKeys.reduce((s, y) => s + Object.values(grupos.porAnio[y]).reduce((t, a) => t + a.length, 0), 0);

  const Seccion = ({ titulo, rows }) => (
    <div className="space-y-2" data-testid={`cot-seccion-${titulo.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">{titulo}</h3>
        <Badge variant="secondary">{rows.length}</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground pb-2">No hay cotizaciones en este periodo.</p>
      ) : (
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Cliente</TableHead><TableHead>Fecha</TableHead><TableHead>Vendedor</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Estatus</TableHead><TableHead>Nota</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs font-semibold">{c.cot_no}{c.revision > 0 && <Badge variant="outline" className="ml-1.5 font-sans text-[10px] px-1.5 py-0" title={`Corregida el ${fechaMX(c.revisado_at)}`} data-testid={`cot-rev-${c.id}`}>Rev. {c.revision}</Badge>}</TableCell>
                  <TableCell className="font-medium">{c.cliente}</TableCell>
                  <TableCell>{fechaMX(c.fecha)}</TableCell>
                  <TableCell>{c.profiles?.full_name || "-"}</TableCell>
                  <TableCell className="text-right">{money(c.subtotal)}</TableCell>
                  <TableCell className="text-right">{money(c.total)}</TableCell>
                  <TableCell><Badge className={badgeFor(c.estatus)}>{c.estatus}</Badge></TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" title={c.nota ? "Ver / editar nota" : "Agregar nota"} onClick={() => abrirNota(c)} data-testid={`cot-nota-${c.id}`} className={c.nota ? "text-amber-600" : "text-muted-foreground"}>
                      <StickyNote className="h-4 w-4" />
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="icon" title="Ver" onClick={() => setDetail(c)} data-testid={`cot-ver-${c.id}`}><Eye className="h-4 w-4" /></Button>
                      {(c.estatus === "Pendiente de autorización" || c.estatus === "Rechazada") && (
                        <Button variant="outline" size="icon" title="Editar / corregir" className="text-blue-600" onClick={() => navigate(`/cotizador?editar=${c.id}`)} data-testid={`cot-editar-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      )}
                      {c.estatus === "Pendiente de autorización" && (
                        <>
                          <Button variant="outline" size="icon" title="Autorizar" className="text-emerald-600" onClick={() => cambiarEstatus(c.id, "Autorizada por el cliente")} data-testid={`cot-autorizar-${c.id}`}><Check className="h-4 w-4" /></Button>
                          <Button variant="outline" size="icon" title="Rechazar" className="text-red-600" onClick={() => abrirNota(c, true)} data-testid={`cot-rechazar-${c.id}`}><X className="h-4 w-4" /></Button>
                        </>
                      )}
                      {isAdmin && c.estatus === "Autorizada por el cliente" && (
                        <Button variant="outline" size="icon" title="Revertir a cotización" className="text-amber-600" onClick={() => revertir(c)} data-testid={`cot-revertir-${c.id}`}><RotateCcw className="h-4 w-4" /></Button>
                      )}
                      {isAdmin && <Button variant="destructive" size="icon" title="Eliminar" onClick={() => eliminar(c.id)}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="cotizaciones-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Cotizaciones</h1>
        <p className="text-muted-foreground mt-2">Historial y autorización de cotizaciones.</p>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Buscar por folio, cliente o estatus..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cot-search" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {isAdmin && <FiltroSelect value={vendSel} onChange={setVendSel} todosLabel="Todos los vendedores" options={vendedores} testid="cot-filtro-vendedor" />}
            <FiltroSelect value={estSel} onChange={setEstSel} todosLabel="Todos los estatus" options={COT_EST} testid="cot-filtro-estatus" className="w-56" />
            {isAdmin && (
            <ReporteModulo
              titulo="REPORTE DE COTIZACIONES"
              filenameBase="Reporte cotizaciones"
              prefix="rep-cot"
              triggerTestId="cot-reporte-btn"
              buttonVariant="outline"
              rows={visibles}
              getFecha={grupoFecha}
              groupBy={(c) => c.profiles?.full_name || "Sin asignar"}
              filtros={[{ label: "Vendedor", value: labelDe(vendSel, vendedores) }, { label: "Estatus", value: labelDe(estSel, COT_EST) }, { label: "Búsqueda", value: search }]}
              columns={[
                { header: "Folio", value: (c) => c.cot_no, mono: true },
                { header: "Cliente", value: (c) => c.cliente },
                { header: "Fecha", value: (c) => fechaMX(c.fecha) },
                { header: "Vendedor", value: (c) => c.profiles?.full_name || "-" },
                { header: "Subtotal", align: "right", value: (c) => money(c.subtotal) },
                { header: "Total", align: "right", value: (c) => money(c.total) },
                { header: "Estatus", value: (c) => c.estatus },
              ]}
              subtotal={(rows) => money(rows.reduce((s, c) => s + Number(c.total || 0), 0))}
              totales={(rows) => [
                { label: "Total subtotal", value: money(rows.reduce((s, c) => s + Number(c.subtotal || 0), 0)) },
                { label: "Total con IVA", value: money(rows.reduce((s, c) => s + Number(c.total || 0), 0)) },
              ]}
            />
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-center text-muted-foreground py-12">Cargando...</p>
          ) : (
            <>
              <Seccion titulo="Mes en curso" rows={grupos.mesEnCurso} />

              <Accordion type="multiple" className="border rounded-md divide-y">
                <AccordionItem value="meses-anio" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-meses-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Meses anteriores ({grupos.curY})</span><Badge variant="secondary">{countMesesAnio}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-2">
                    {mesesAnioKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No hay cotizaciones en meses anteriores de este año.</p>
                    ) : (
                      mesesAnioKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${grupos.curY}`} rows={grupos.mesesAnio[m]} />)
                    )}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="anios" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-anios-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Años anteriores</span><Badge variant="secondary">{countAnios}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    {aniosKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No hay cotizaciones de años anteriores.</p>
                    ) : (
                      <Accordion type="multiple" className="border rounded-md divide-y">
                        {aniosKeys.map((y) => {
                          const mesesKeys = Object.keys(grupos.porAnio[y]).map(Number).sort((a, b) => a - b);
                          const countY = mesesKeys.reduce((s, m) => s + grupos.porAnio[y][m].length, 0);
                          return (
                            <AccordionItem key={y} value={y} className="border-0 px-4">
                              <AccordionTrigger data-testid={`acc-anio-${y}`} className="hover:no-underline">
                                <span className="flex items-center gap-2"><span className="font-semibold">{y}</span><Badge variant="secondary">{countY}</Badge></span>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-6 pt-2">
                                {mesesKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${y}`} rows={grupos.porAnio[y][m]} />)}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {grupos.sinFecha.length > 0 && <Seccion titulo="Sin fecha" rows={grupos.sinFecha} />}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detailBase} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[90dvh] overflow-y-auto overflow-x-auto [&>button.absolute]:hidden">
          <DialogHeader>
            <div className="flex justify-between items-center pr-8">
              <div>
                <DialogTitle>Cotización {detail?.cot_no}{versionSel ? ` · Versión anterior (Rev. ${versionSel.revision})` : ""}</DialogTitle>
                <DialogDescription>{detail?.cliente} · {fechaMX(detail?.fecha)}</DialogDescription>
                {(detailBase?.versiones || []).length > 0 && (
                  <div className="flex items-center gap-2 mt-2" data-testid="cot-versiones">
                    <History className="h-4 w-4 text-muted-foreground" />
                    <Select value={verIdx} onValueChange={setVerIdx}>
                      <SelectTrigger className="h-8 w-80" data-testid="cot-version-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="actual">Versión actual · Rev. {detailBase.revision} ({fechaMX(detailBase.revisado_at)}) · {money(detailBase.total)}</SelectItem>
                        {detailBase.versiones.map((v, i) => ({ v, i })).reverse().map(({ v, i }) => (
                          <SelectItem key={i} value={String(i)}>{v.revision ? `Rev. ${v.revision}` : "Original"} ({fechaMX(v.revisado_at || v.fecha)}) · {money(v.total)} · corregida por {v.reemplazada_por || "—"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" className="text-green-600 border-green-600 hover:bg-green-50 hover:text-green-700" onClick={() => { downloadCotizacionPdf(bodyRef.current, headerRef.current, docName); openWhatsApp("", `Hola ${cli?.nombre_contacto || detail?.cliente || ""}, le compartimos la cotización ${detail?.cot_no} de Duelas Cooper Slip por un total de ${money(detail?.total)} (IVA incluido)${detail?.vigencia ? `, con vigencia de ${detail.vigencia}` : ""}. Le adjuntamos el PDF. ¡Gracias!`); }} data-testid="cot-whatsapp"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</Button>
                <Button variant="outline" size="sm" onClick={() => downloadCotizacionPdf(bodyRef.current, headerRef.current, docName)} data-testid="cot-descargar-pdf"><Download className="h-4 w-4 mr-2" /> Descargar PDF</Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="cot-imprimir"><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
                <Button variant="outline" size="sm" onClick={() => setDetail(null)} data-testid="cot-cerrar"><X className="h-4 w-4 mr-2" /> Cerrar</Button>
              </div>
            </div>
          </DialogHeader>
          {detail && (
            <DocumentoVenta
              detail={detail} cli={cli} listas={listas} printRef={printRef} headerRef={headerRef} bodyRef={bodyRef}
              encabezadoExtra={<>
                {detail.revision > 0 && <p className="text-sm" data-testid="cot-detail-revision"><span className="font-bold">Revisión {detail.revision}:</span> {fechaMX(detail.revisado_at)}</p>}
                {versionSel && <p className="text-xs font-bold text-red-600 uppercase">Versión anterior · sin validez</p>}
              </>}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!notaCot} onOpenChange={(o) => { if (!o) cerrarNota(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{notaCot?.rechazar ? "Motivo del rechazo" : "Nota de la cotización"}</DialogTitle>
            <DialogDescription>
              {notaCot?.rechazar
                ? `Explica la causa por la que se rechaza la cotización ${notaCot?.cot_no}.`
                : `Registra o edita la nota de la cotización ${notaCot?.cot_no}.`}
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={5} value={notaTexto} onChange={(e) => setNotaTexto(e.target.value)} placeholder="Escribe aquí la causa o la nota..." data-testid="cot-nota-textarea" autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={cerrarNota} data-testid="cot-nota-cancelar">Cancelar</Button>
            <Button onClick={guardarNota} disabled={notaSaving} data-testid="cot-nota-guardar" className={notaCot?.rechazar ? "bg-red-600 hover:bg-red-700" : ""}>
              {notaSaving ? "Guardando..." : notaCot?.rechazar ? "Rechazar y guardar" : "Guardar nota"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
